import { Button, Message, Spacer } from '@kvdbil/components';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { sessionStartSuccess } from '~/App/shared/actions/session';
import { trackGA4AccountCreated } from '~/helpers/client/ga4TrackEvent';
import { createMember } from '~/helpers/orchestration/member';
import { runWithRecaptcha } from '~/helpers/recaptcha';
import { formErrors } from '~/helpers/validation';
import { useTranslation } from '~/Locale';
import {
  CountryField,
  EmailField,
  FirstNameField,
  LastNameField,
  NationalIdentificationField,
  PasswordField
} from '../../../../Fields/shared';
import { useRecaptcha } from '../../../hooks/authHooks';
import { TermsCheckbox } from '../TermsCheckbox';
import useLocalization from '~/App/shared/hooks/useLocalization';
import { StyledCheckboxContainer } from '../StyledCheckboxContainer';
import { NewsletterCheckboxes } from '../NewsletterCheckboxes';
import { createEmailSubscriptionNames } from '~/App/shared/helpers/general';

const FormComponent = styled.form`
  display: grid;
  grid-row-gap: 1rem;
  text-align: left;
`;

type Props = {
  onSuccess(): void;
};

const PrivateRegistrationForm = ({ onSuccess }: Props) => {
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<string | string[] | null>(null);
  const dispatch = useDispatch();
  const { isSwedishLocale } = useLocalization();

  useRecaptcha();

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      nationalIdentificationNumber: '',
      acceptsMinimumAgeRequirement: false,
      country: isSwedishLocale
        ? { value: 'se', label: t('SWEDEN') }
        : { value: '', label: '' },
      personalCarSubscription: true,
      heavyVehiclesSubscription: true
    }
  });

  const onSubmit = useCallback(
    (googleResponse: string) => {
      const values = getValues();
      const { personalCarSubscription, heavyVehiclesSubscription } = values;

      const emailSubscriptionNames = createEmailSubscriptionNames({
        personalCarSubscription,
        heavyVehiclesSubscription
      });

      const nationalIdentificationData =
        values?.nationalIdentificationNumber && values.country.value !== 'se'
          ? {
              nationalIdentificationCountry: values.country.value,
              nationalIdentificationNumber: values.nationalIdentificationNumber
            }
          : {};

      const memberData = {
        person: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          acceptsMinimumAgeRequirement: values.acceptsMinimumAgeRequirement,
          ...nationalIdentificationData
        },
        termsAccepted: true,
        addresses: [{ country: values?.country?.value }],
        emailSubscriptionNames
      };

      createMember({
        member: memberData,
        gRecaptchaResponse: googleResponse
      })
        .then(data => {
          trackGA4AccountCreated({
            user_id: data.member.id,
            user_type: 'private',
            is_car_dealer: data.member.person.isCarDealer
          });

          dispatch(sessionStartSuccess(data));

          onSuccess();
        })
        .catch((error: Record<string, string>[]) => {
          const errorMessage = formErrors(
            { errors: error },
            'response.data.message',
            true
          );
          setError(errorMessage[0]);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [dispatch, getValues, onSuccess]
  );

  const submitWithRecaptcha = useCallback(() => {
    setLoading(true);
    setError(null);

    runWithRecaptcha(googleResponse => onSubmit(googleResponse));
  }, [onSubmit, setLoading, setError]);

  const country = watch('country');

  return (
    <div>
      <FormComponent onSubmit={handleSubmit(submitWithRecaptcha)} noValidate>
        <FirstNameField control={control} t={t} />

        <LastNameField control={control} t={t} />

        <EmailField control={control} t={t} />

        <PasswordField control={control} t={t} autoComplete="new-password" />

        <CountryField control={control} t={t} />

        {country.value !== 'se' && (
          <NationalIdentificationField
            control={control}
            t={t}
            label={t('National identification number (optional)')}
            rules={{
              required: false,
              pattern: undefined,
              maxLength: undefined
            }}
            helperText={t(
              'Entering this information will speed up the process when buying a vehicle.'
            )}
          />
        )}

        <Spacer height={0.5} />

        <StyledCheckboxContainer>
          <TermsCheckbox control={control} />
        </StyledCheckboxContainer>

        <NewsletterCheckboxes control={control} />

        {error && (
          <Message fullWidth withIcon type="error">
            {error}
          </Message>
        )}

        <Button
          color="secondary"
          size="regular"
          fullWidth
          isLoading={isLoading}
          data-test="register-submit"
          isDisabled={!isValid}
          type="submit"
        >
          {t('Register')}
        </Button>
      </FormComponent>
    </div>
  );
};

export default PrivateRegistrationForm;
